import { CarePlanNote } from "@/lib/types"

/**
 * Which rung of the ladder actually worked.
 *
 * "formatted" put both flavors on the clipboard, so a rich-text note field
 * keeps the layout and a plain-text one still gets readable text.
 * "plainOnly" means the formatted write was refused and only text got through.
 */
export type CopyOutcome = "formatted" | "plainOnly" | "failed"

/**
 * Put a care plan note on the clipboard in both flavors at once.
 *
 * The EHR's note editor decides which one it reads, so writing both is what
 * lets a single button work across NextGen Office, eCW, Athena and Elation
 * without asking anyone to pick a format.
 *
 * Browsers disagree about when a two-flavor write is allowed - it needs a
 * secure context, an unexpired user gesture and, in Firefox, a recent enough
 * version - so each rung falls back to the next rather than failing outright.
 */
export async function copyNoteToEhr(note: Pick<CarePlanNote, "html" | "text">): Promise<CopyOutcome> {
  // 1. Both flavors, the modern way.
  try {
    if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([note.html], { type: "text/html" }),
          "text/plain": new Blob([note.text], { type: "text/plain" }),
        }),
      ])
      return "formatted"
    }
  } catch {
    // fall through
  }

  // 2. Both flavors via a selection copy - byte for byte what the browser
  //    produces when someone selects the note and presses Ctrl+C.
  const stage = document.createElement("div")
  stage.setAttribute("aria-hidden", "true")
  stage.style.cssText = "position:fixed;left:-9999px;top:0;width:700px"
  stage.innerHTML = note.html
  document.body.appendChild(stage)
  try {
    const range = document.createRange()
    range.selectNodeContents(stage)
    const selection = window.getSelection()
    selection?.removeAllRanges()
    selection?.addRange(range)
    const ok = document.execCommand("copy")
    selection?.removeAllRanges()
    if (ok) return "formatted"
  } catch {
    // fall through
  } finally {
    stage.remove()
  }

  // 3. Text only. The note loses its formatting but still reads correctly,
  //    because every value in it carries its own label.
  try {
    await navigator.clipboard.writeText(note.text)
    return "plainOnly"
  } catch {
    // fall through
  }

  const textarea = document.createElement("textarea")
  textarea.value = note.text
  textarea.setAttribute("aria-hidden", "true")
  textarea.style.cssText = "position:fixed;left:-9999px;top:0"
  document.body.appendChild(textarea)
  try {
    textarea.select()
    if (document.execCommand("copy")) return "plainOnly"
  } catch {
    // fall through
  } finally {
    textarea.remove()
  }

  return "failed"
}
